import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { useAddPermissionMutation } from "permissions/_api/permissions-api-service";
import { levelOptions } from "permissions/_views/grant-row/grant-row-model";
import { PeoplePicker } from "permissions/_views/records/people-picker";
import { recordResource } from "permissions/_views/records/record-grants-model";
import type { PermissionGranteeOption } from "permissions/_views/search/use-permission-grantee-typeahead";
import { recordPage } from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import type { AccessLevel } from "permissions/resources/permission-resource-types";
import { useState } from "react";

/**
 * "+ Add people" on a record page: several people at once, with the kind and the record fixed and one
 * level for all (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Adding, removing, changing").
 *
 * The API grants one person per request, so each person is a request of their own. A partial failure
 * keeps the dialog open on the people it failed for, each with the server's message, and Retry sends
 * just those again.
 */

export type AddPeopleFailure = Readonly<{ person: PermissionGranteeOption; message: string }>;

/** What one round of grants came to: who was granted, and who was not and why. */
export type AddPeopleOutcome = Readonly<{ granted: readonly PermissionGranteeOption[]; failed: readonly AddPeopleFailure[] }>;

/** Sends one grant per person, all at once, and sorts the answers into granted and failed. */
export async function grantEach(
  people: readonly PermissionGranteeOption[],
  grantOne: (person: PermissionGranteeOption) => Promise<unknown>,
): Promise<AddPeopleOutcome> {
  const answers = await Promise.allSettled(people.map((person) => grantOne(person)));
  const granted: PermissionGranteeOption[] = [];
  const failed: AddPeopleFailure[] = [];
  answers.forEach((answer, index) => {
    if (answer.status === "fulfilled") granted.push(people[index]);
    else failed.push({ person: people[index], message: getErrorMessageFromUnknown(answer.reason) ?? "The grant was refused." });
  });
  return { granted, failed };
}

export type AddPeopleDialogProps = Readonly<{
  open: boolean;
  kind: PermissionKind;
  /** The record every grant is on. */
  targetId: string;
  /** The record's name, when the viewer may read it. */
  recordName?: string;
  /** People already chosen when the dialog opens, such as the person Check access found without a grant. */
  initialPeople?: readonly PermissionGranteeOption[];
  /** The level the dialog opens at, such as the Can edit an Edit check found missing. The kind's default otherwise. */
  initialAccessLevel?: AccessLevel;
  legalEntityId: Guid;
  viewerUserId: Guid;
  mayAdministerPlatform: boolean;
  onClose: () => void;
  /** Called with everyone granted, after each round that granted anyone. */
  onGranted?: (people: readonly PermissionGranteeOption[]) => void;
}>;

export default function AddPeopleDialog(props: AddPeopleDialogProps) {
  const { kind } = props;
  const noun = kind.targetNoun ?? "record";
  const options = levelOptions(kind, {}, props.mayAdministerPlatform);
  const defaultIndex = Math.max(
    0,
    options.findIndex((option) => option.level.accessLevel === (props.initialAccessLevel ?? kind.default?.accessLevel ?? undefined)),
  );
  const [people, setPeople] = useState<readonly PermissionGranteeOption[]>(props.initialPeople ?? []);
  const [levelIndex, setLevelIndex] = useState(defaultIndex);
  const [failed, setFailed] = useState<readonly AddPeopleFailure[]>([]);
  const [working, setWorking] = useState(false);
  const [addPermission] = useAddPermissionMutation();

  const close = () => {
    if (working) return;
    setPeople([]);
    setFailed([]);
    props.onClose();
  };

  const submit = async (chosen: readonly PermissionGranteeOption[]) => {
    setWorking(true);
    const level = options[levelIndex]?.level;
    const outcome = await grantEach(chosen, (person) =>
      addPermission({
        permission: new Permission({
          resource: recordResource(kind, level, props.targetId),
          granteeUserId: new Guid(person.value),
          grantorUserId: props.viewerUserId,
          entityId: props.legalEntityId,
          version: 0,
        }),
      }).unwrap(),
    );
    setWorking(false);
    if (outcome.granted.length > 0) {
      props.onGranted?.(outcome.granted);
      enqueueSnackbar(recordPage.added(kind.name, outcome.granted.length, chosen.length), {
        variant: outcome.failed.length === 0 ? "success" : "warning",
        role: "status",
      });
    }
    if (outcome.failed.length === 0) {
      setPeople([]);
      setFailed([]);
      props.onClose();
      return;
    }
    // Only the people it failed for stay chosen, so Retry sends just those.
    setPeople(outcome.failed.map((failure) => failure.person));
    setFailed(outcome.failed);
  };

  const title = props.recordName ? recordPage.addPeopleTitle(noun, props.recordName) : recordPage.addPeopleTitleUnnamed(noun);
  const retrying = failed.length > 0;

  return (
    <Dialog open={props.open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="add-people-title">
      <DialogTitle id="add-people-title" sx={{ overflowWrap: "anywhere" }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {retrying && (
            <Alert severity="warning">
              {recordPage.someFailed(failed.length)}
              <Stack component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {failed.map((failure) => (
                  <li key={failure.person.value}>{`${failure.person.label}: ${failure.message}`}</li>
                ))}
              </Stack>
            </Alert>
          )}
          <PeoplePicker
            label={recordPage.people}
            value={people}
            onChange={(chosen) => {
              setPeople(chosen);
              setFailed([]);
            }}
            disabled={working}
          />
          {options.length > 1 && (
            <FormControl size="small" disabled={working}>
              <InputLabel id="add-people-level">{recordPage.level}</InputLabel>
              <Select
                labelId="add-people-level"
                label={recordPage.level}
                value={levelIndex}
                onChange={(event) => setLevelIndex(Number(event.target.value))}
              >
                {options.map((option, index) => (
                  <MenuItem key={option.label} value={index}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={working}>
          Cancel
        </Button>
        <Button variant="contained" disabled={people.length === 0 || working} onClick={() => void submit(people)}>
          {retrying ? recordPage.retryFailed : recordPage.grant(people.length)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
